/**
 * Post-login destination.
 *
 * Legacy (`login.component.ts` onSubmit, `verify-code.component.ts` after the
 * code, and MyAccess `login.js` `shouldRedirectToDashboardV2`) sends the person
 * to the stored `redirectUrl` / `return_to` when one was captured. Dashboard,
 * Properties, or Orders is only the fallback when that target is missing.
 * A target that is the dashboard itself still goes through `LandingRedirect`
 * so a Base-tier account is not dropped on a page they cannot use.
 *
 * `returnTo` is same-app only. Absolute URLs, protocol-relative URLs, and
 * backslash or encoded-slash tricks fall back to today's default page.
 */

/** Default post-authentication path. `/` runs `LandingRedirect`. */
export const DEFAULT_POST_AUTH_PATH = '/' as const;

/** Router basename. Must match `basename` in `src/routes/index.tsx`. */
const ROUTER_BASENAME = '/v2';

/** Cap a return target so a huge query cannot be used as a redirect payload. */
const RETURN_PATH_MAX_LENGTH = 2000;

/** Decode at most twice so `%252F%252F` cannot survive as a protocol-relative URL. */
const RETURN_PATH_DECODE_PASSES = 2;

const SCHEME_PREFIX = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Auth pages are not useful email targets. Sending someone back to sign-in
 * after they just signed in loops the bounce.
 */
const AUTH_RETURN_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/set-password',
  '/mfa/verify',
  '/auth',
] as const;

/**
 * True when `value` contains a backslash, a null, or another control character.
 */
function hasUnsafeCharacter(value: string): boolean {
  if (value.includes('\\') || value.includes('\0')) return true;
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * Decode percent-encoding a bounded number of times.
 *
 * @returns The decoded string, or null when a `%` sequence is malformed.
 */
function decodeReturnPath(raw: string): string | null {
  let current = raw;
  for (let pass = 0; pass < RETURN_PATH_DECODE_PASSES; pass += 1) {
    if (!current.includes('%')) break;
    try {
      const next = decodeURIComponent(current);
      if (next === current) break;
      current = next;
    } catch {
      return null;
    }
  }
  return current;
}

/**
 * True when `pathname` is a sign-in or account-setup screen.
 */
function isAuthReturnSurface(pathname: string): boolean {
  return AUTH_RETURN_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Strip one leading `/v2` so `navigate` does not double the router basename.
 */
function stripRouterBasename(path: string): string {
  if (path === ROUTER_BASENAME) return '/';
  if (
    path.startsWith(`${ROUTER_BASENAME}/`) ||
    path.startsWith(`${ROUTER_BASENAME}?`) ||
    path.startsWith(`${ROUTER_BASENAME}#`)
  ) {
    const rest = path.slice(ROUTER_BASENAME.length);
    return rest.startsWith('/') ? rest : `/${rest}`;
  }
  return path;
}

/**
 * Same-app relative path, or null when the value is missing or unsafe.
 *
 * Allows `/orders/44870` and `/properties/7645?tab=reports&reportId=1#section`.
 * Rejects absolute URLs, protocol-relative URLs, and backslash tricks.
 */
export function safeSameAppReturnPath(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > RETURN_PATH_MAX_LENGTH) return null;
  const decoded = decodeReturnPath(trimmed);
  if (decoded == null) return null;
  const path = decoded.trim();
  if (!path || path.length > RETURN_PATH_MAX_LENGTH) return null;
  if (SCHEME_PREFIX.test(path) || path.includes('://')) return null;
  if (path.startsWith('//') || path.startsWith('\\') || path.startsWith('/\\')) return null;
  if (!path.startsWith('/')) return null;
  if (hasUnsafeCharacter(path)) return null;

  const stripped = stripRouterBasename(path);
  if (!stripped.startsWith('/') || stripped.startsWith('//')) return null;
  if (hasUnsafeCharacter(stripped) || stripped.includes('://')) return null;
  return stripped;
}

/**
 * Normalize a post-login destination.
 *
 * Query strings and hashes are preserved (property tabs and report ids).
 * `/dashboard` routes through `/` so `LandingRedirect` applies tier dispatch.
 * Missing or unsafe values use {@link DEFAULT_POST_AUTH_PATH}.
 */
export function normalizePostAuthPath(path: string | undefined): string {
  const safe = safeSameAppReturnPath(path);
  if (!safe) return DEFAULT_POST_AUTH_PATH;
  const pathnameOnly = safe.split(/[?#]/, 1)[0] ?? safe;
  if (pathnameOnly === '/dashboard' || isAuthReturnSurface(pathnameOnly)) {
    return DEFAULT_POST_AUTH_PATH;
  }
  return safe;
}

/**
 * In-router sign-in path that keeps a safe return target.
 *
 * @param raw - Candidate `returnTo` (router path, or a browser path that still has `/v2`)
 */
export function loginRouteWithReturn(raw: string | null | undefined): string {
  const safe = normalizePostAuthPath(raw ?? undefined);
  if (safe === DEFAULT_POST_AUTH_PATH) return '/login';
  return `/login?returnTo=${encodeURIComponent(safe)}`;
}

/**
 * Full-browser sign-in URL (`/v2/login`) that keeps a safe return target.
 * Used by the API client, which assigns `window.location` outside the router.
 */
export function loginUrlPreservingReturn(browserPath: string): string {
  const route = loginRouteWithReturn(browserPath);
  if (route === '/login') return `${ROUTER_BASENAME}/login`;
  return `${ROUTER_BASENAME}${route}`;
}

/**
 * In-router 2-step code path. The target stays on the query so it survives
 * the code screen the way legacy kept `redirectUrl` through verify-code.
 */
export function mfaVerifyRoute(returnTo: string | undefined): string {
  const safe = normalizePostAuthPath(returnTo);
  if (safe === DEFAULT_POST_AUTH_PATH) return '/mfa/verify';
  return `/mfa/verify?returnTo=${encodeURIComponent(safe)}`;
}

/**
 * Build a post-auth return path from a react-router location-like object.
 * Includes search and hash so a property tab survives the login bounce.
 */
export function pathWithSearch(
  loc: { pathname?: string; search?: string; hash?: string } | null | undefined,
): string | undefined {
  const pathname = loc?.pathname;
  if (!pathname) return undefined;
  const search = typeof loc.search === 'string' ? loc.search : '';
  const hash = typeof loc.hash === 'string' ? loc.hash : '';
  return safeSameAppReturnPath(`${pathname}${search}${hash}`) ?? undefined;
}
